import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"

// scan_intelligence_controller
//
// Fetches AI-powered next-action suggestions for the scanned asset.
// Renders a "Smart Suggestions" card with 1–3 recommended actions.
// Auto-loads on connect if the user is authenticated.
//
// Usage:
//   <div data-controller="scan-intelligence"
//        data-scan-intelligence-url-value="<url>"
//        data-scan-intelligence-auto-load-value="true">
//     <div data-scan-intelligence-target="container"></div>
//   </div>

export default class extends Controller {
  static targets = ["container"]
  static values = { url: String, autoLoad: { type: Boolean, default: false } }

  connect() {
    if (this.autoLoadValue && this.urlValue) {
      // Delay so the page finishes rendering before triggering the fetch
      setTimeout(() => this.load(), 800)
    }
  }

  async load() {
    if (!this.hasContainerTarget) return
    this.containerTarget.innerHTML = this.#loadingHtml()

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.#csrfToken(),
          "Accept": "application/json",
          "Content-Type": "application/json"
        }
      })

      if (!response.ok) {
        this.containerTarget.innerHTML = ""
        return
      }

      const data = await response.json()
      if (!data.next_actions || data.next_actions.length === 0) {
        this.containerTarget.innerHTML = ""
        return
      }

      this.containerTarget.innerHTML = this.#renderCard(data)
    } catch (e) {
      this.containerTarget.innerHTML = ""
    }
  }

  #loadingHtml() {
    return `
      <div class="bg-violet-50 border border-violet-200 rounded-xl p-4 animate-pulse">
        <div class="flex items-center gap-2 mb-3">
          <div class="w-5 h-5 bg-violet-200 rounded"></div>
          <div class="h-4 bg-violet-200 rounded w-32"></div>
        </div>
        <div class="space-y-2">
          <div class="h-3 bg-violet-100 rounded w-full"></div>
          <div class="h-3 bg-violet-100 rounded w-3/4"></div>
        </div>
      </div>
    `
  }

  #renderCard(data) {
    const actions = data.next_actions || []
    const note = data.compliance_note || ""

    const actionItems = actions.map(action => `
      <li class="flex items-start gap-2 text-sm text-gray-800">
        <span class="material-symbols-outlined text-violet-500 !text-base mt-0.5 shrink-0">arrow_right</span>
        <span>${this.#escape(action)}</span>
      </li>
    `).join("")

    const noteHtml = note ? `
      <div class="mt-3 pt-3 border-t border-violet-200 flex items-start gap-2">
        <span class="material-symbols-outlined text-amber-500 !text-base shrink-0 mt-0.5">info</span>
        <p class="text-sm text-gray-700">${this.#escape(note)}</p>
      </div>
    ` : ""

    return `
      <div class="bg-violet-50 border border-violet-200 rounded-xl p-4">
        <div class="flex items-center gap-2 mb-3">
          <span class="material-symbols-outlined text-violet-600 !text-lg">lightbulb</span>
          <h3 class="text-sm font-semibold text-violet-800">${t("javascript.controllers.scan_intelligence_controller.smart_suggestions")}</h3>
          <span class="ml-auto text-xs text-violet-400 font-medium">AI</span>
        </div>
        <ul class="space-y-2">
          ${actionItems}
        </ul>
        ${noteHtml}
      </div>
    `
  }

  #csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  #escape(str) {
    return String(str)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;")
      .replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
